import { Injectable } from '@angular/core';
import { ApiService } from '@app-shared/services/api.service';
import { APIRequest } from '@app-shared/services/req-res.types';
import { UtilService } from '@app-shared/services/util.service';
import { Observable, of } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class GetAllService {
  private sessionCache = new Map<string, Observable<any>>();

  constructor(
    private api: ApiService,
    private util: UtilService
  ) { }

  get jwt(): string | null {
    return localStorage.getItem('JwtToken');
  }
  get orgID(): string | null {
    const value = localStorage.getItem('OrgId');
    if (value) {
      try {
        const decrypted = this.util.decrypt(value);
        return decrypted || value;
      } catch {
        return value;
      }
    }
    return null;
  }

  clearSessionCache(module?: string) {
    if (module) {
      for (const key of Array.from(this.sessionCache.keys())) {
        if (key.startsWith(module + '_') || key === module) {
          this.sessionCache.delete(key);
        }
      }
      // Also clear browser-level cache for industryVertical
      try {
        if (module === 'industryVertical') {
          const storageKey = `cache_industryVertical_${this.orgID || 0}`;
          localStorage.removeItem(storageKey);
        }
      } catch (e) {
        // ignore storage errors
      }
    } else {
      this.sessionCache.clear();
    }
  }

  getAll(module: any, data?: any) {
    const cacheableModules = ['businessVertical', 'industryVertical', 'product', 'statesAndDistricts'];
    const cacheKey = `${module}_${this.orgID || 0}`;

    // For industryVertical, try browser localStorage cache first (persist across reloads)
    if (module === 'industryVertical') {
      try {
        const storageKey = `cache_industryVertical_${this.orgID || 0}`;
        const raw = localStorage.getItem(storageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.data) {
            return of(parsed.data);
          }
        }
      } catch (e) {
        // ignore parse/storage errors and fall back to network
      }
    }

    if (cacheableModules.includes(module) && this.sessionCache.has(cacheKey)) {
      return this.sessionCache.get(cacheKey)!;
    }

    var postData: APIRequest;
    var postURL = '/getall/' + module;
    switch (module) {
      case 'courseFrameworks':
      case 'indVerticals':
      case 'modules':
      case 'statesAndDistricts':
        postData = {};
        break;
      case 'orgs':
        // Support pagination for orgs with optional data parameters
        postData = data && (data.pageIndex !== undefined || data.pageSize !== undefined) ? {
          requestBody: {
            pageIndex: data.pageIndex || 0,
            pageSize: data.pageSize || 10
          }
        } : {};
        break;
      case 'industryVertical':
      case 'businessVertical':
      case 'product':
      case 'courseGroup':
        postData = {
          requestBody: {
            organization_id: parseInt(this.orgID, 10),
          }
        }
        break;
      case 'courseSubGroup':
      case 'courseFrameworksByOrgId':
        postData = {
          requestBody: {
            organization_id: parseInt(this.orgID, 10),
          }
        }
        break;
      case 'admin':
        postData = {
          requestBody: {
            organization_id: data,
          }
        }
        break;
      case 'orgUsers':
      case 'salesLead':
      case 'userCategories':
        postData = {
          requestBody: {
            organization_id: parseInt(this.orgID, 10),
          }
        }
        break;
      case 'subscriptionPlans':
      case 'getFeaturesByPlan':
        postData = {
          requestBody: {
            plan_id: data
          }
        }
      default:
        break;
    }

    const req$ = this.api.post(postURL, postData);
    if (cacheableModules.includes(module)) {
      // For industryVertical also persist into localStorage when response arrives
      if (module === 'industryVertical') {
        const storageKey = `cache_industryVertical_${this.orgID || 0}`;
        const cached$ = req$.pipe(
          tap((resp: any) => {
            try {
              const payload = { ts: Date.now(), data: resp };
              localStorage.setItem(storageKey, JSON.stringify(payload));
            } catch (e) {
              // ignore storage errors
            }
          }),
          shareReplay(1)
        );
        this.sessionCache.set(cacheKey, cached$);
        return cached$;
      }

      const cached$ = req$.pipe(shareReplay(1));
      this.sessionCache.set(cacheKey, cached$);
      return cached$;
    }

    return req$;
  }

  getAdminRoles() {
    var postURL = '/getall/adminRoles';
    const parsedOrgId = this.orgID ? parseInt(this.orgID, 10) : -1;
    let postData = {
      requestBody: {
        organization_id: !isNaN(parsedOrgId) ? parsedOrgId : -1,
      }
    };
    return this.api.post(postURL, postData);
  }

  // Fetch admin roles for a specific organization id (useful for org -1)
  getAdminRolesByOrg(param: any) {
    var postURL = '/getall/adminRoleByName';
    let postData = {
      requestBody: {
        organization_id: param.orgId,
        role_name: param.role_name,
      }
    }
    return this.api.post(postURL, postData);
  }

  getAdmin(id){
    var postURL = '/getallAdmin';
    let postData = {
      requestBody: {
        orgID: parseInt(id, 10),
      }

    }
    return this.api.post(postURL, postData);
  }

  // Fetch user categories for a specific organization id
  getUserCategoriesByOrg(param: any) {
    console.log('getUserCategoriesByOrg param:', param);
    var postURL = '/getall/userCategoryByName';
    let postData = {
      requestBody: {
        organization_id: param.orgId,
        category_name: param.category_name,
      }
    }
    return this.api.post(postURL, postData);
  }

  sendTicketMail(data: any) {
    const postURL = '/sendTicketEmail';
    const postData: APIRequest = {
      requestBody: {
        id: data?.id || data?.ticket_id,
        ticket_id: data?.id || data?.ticket_id,
        cc_emails: data?.cc_emails,
        description: data?.description || data?.description_text || data?.detail || '',
        subject: data?.subject || data?.summary || '',
        priority: data?.priority,
        status: data?.status,
        assigned_to: data?.assigned_to || data?.assignedTo || '',
        org_id: data?.org_id || data?.orgId,
        email: data?.email
      }
    };
    return this.api.post(postURL, postData);
  }
}